import {
  DIFFICULTIES,
  STRATEGIES,
  type BotDifficulty,
  type BotSpec,
  type BotStrategy,
} from '@halvard/ai';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useBotDriver } from '../bot/useBotDriver';
import { playersFromParams, playersParams } from '../game/botParams';
import { LocalGame } from '../game/LocalGame';
import {
  EXPERIMENTS,
  flagsOf,
  mountainFromParams,
  mountainKey,
  mountainParams,
  randomSeed,
  toggleExperiment,
  withExperiment,
  type ExperimentKey,
  type Mountain,
} from '../game/mountain';
import { createLocalGameStore, type LocalGameStoreApi, type Players } from '../game/store';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useStore } from 'zustand';
import './PlayBot.css';

/** `/mountain/bot`: play against a bot, or watch two bots play each other. */
export function PlayBot() {
  const [params, setParams] = useSearchParams();
  const players = playersFromParams(params);
  const mountain = mountainFromParams(params);
  useDocumentTitle(
    players?.I.kind === 'bot' && players.B.kind === 'bot' ? 'Bot vs bot' : 'Play the bot',
  );

  if (!players || !mountain)
    return (
      <BotSetup onStart={(p, m) => setParams({ ...playersParams(p), ...mountainParams(m) })} />
    );

  const go = (next: Mountain) => setParams({ ...playersParams(players), ...mountainParams(next) });
  return (
    <BotGame
      key={`${mountainKey(mountain)}|${playersKey(players)}`}
      players={players}
      mountain={mountain}
      onMountain={go}
    />
  );
}

function playersKey(players: Players): string {
  return new URLSearchParams(playersParams(players)).toString();
}

function BotGame({
  players,
  mountain,
  onMountain,
}: {
  players: Players;
  mountain: Mountain;
  onMountain: (m: Mountain) => void;
}) {
  const watching = players.I.kind === 'bot' && players.B.kind === 'bot';
  const [store] = useState(() =>
    createLocalGameStore(mountain, {
      players,
      botSeed: randomSeed(),
      botDelay: watching ? 600 : 350,
    }),
  );
  useBotDriver(store);
  return (
    <LocalGame
      store={store}
      onToggleExperiment={(key) => onMountain(toggleExperiment(mountain, key))}
      onNewMountain={() => onMountain({ ...flagsOf(mountain), kind: 'seed', seed: randomSeed() })}
      onClassic={() =>
        mountain.kind === 'classic'
          ? store.getState().start(mountain)
          : onMountain({ ...flagsOf(mountain), kind: 'classic' })
      }
      panelExtra={<BotControls store={store} watching={watching} />}
    />
  );
}

const SPEEDS = [
  { label: 'Slow', ms: 1500 },
  { label: 'Normal', ms: 600 },
  { label: 'Fast', ms: 80 },
];

function BotControls({ store, watching }: { store: LocalGameStoreApi; watching: boolean }) {
  const s = useStore(store);
  const over = s.game.winReason !== null;
  return (
    <div className="game-section">
      {watching && (
        <>
          <h2 className="label">Bot vs bot</h2>
          <div className="game-row">
            <button
              type="button"
              className="btn btn-small btn-primary"
              onClick={() => s.setPaused(!s.paused)}
              disabled={over}
            >
              {s.paused ? 'Resume' : 'Pause'}
            </button>
            <button type="button" className="btn btn-small" onClick={s.step} disabled={over}>
              Step one move
            </button>
          </div>
          <p className="label bot-subhead">Speed</p>
          <div className="segmented" role="group" aria-label="Speed">
            {SPEEDS.map((speed) => (
              <button
                key={speed.label}
                type="button"
                aria-pressed={s.botDelay === speed.ms}
                onClick={() => s.setBotDelay(speed.ms)}
              >
                {speed.label}
              </button>
            ))}
          </div>
          {s.game.config.fog && (
            <>
              <p className="label bot-subhead">Watch as</p>
              <div className="segmented" role="group" aria-label="Watch as">
                {(
                  [
                    ['all', 'Everything'],
                    ['I', 'Ivory'],
                    ['B', 'Basalt'],
                  ] as const
                ).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    aria-pressed={s.perspective === id}
                    onClick={() => s.setPerspective(id)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </>
          )}
        </>
      )}
      <p className="bot-change">
        <Link to="/mountain/bot">Change bots or sides</Link>
      </p>
    </div>
  );
}

const DIFFICULTY_IDS = Object.keys(DIFFICULTIES) as BotDifficulty[];
const STRATEGY_IDS = Object.keys(STRATEGIES) as BotStrategy[];

function BotPicker({
  label,
  value,
  onChange,
}: {
  label: string;
  value: BotSpec;
  onChange: (spec: BotSpec) => void;
}) {
  return (
    <fieldset className="bot-picker">
      <legend>{label}</legend>
      <p className="label bot-subhead">Difficulty</p>
      <div className="segmented" role="group" aria-label={`${label} difficulty`}>
        {DIFFICULTY_IDS.map((id) => (
          <button
            key={id}
            type="button"
            aria-pressed={value.difficulty === id}
            onClick={() => onChange({ ...value, difficulty: id })}
            title={DIFFICULTIES[id].description}
          >
            {DIFFICULTIES[id].name}
          </button>
        ))}
      </div>
      <p className="label bot-subhead">Strategy</p>
      <div className="bot-strategies" role="radiogroup" aria-label={`${label} strategy`}>
        {STRATEGY_IDS.map((id) => (
          <button
            key={id}
            type="button"
            role="radio"
            aria-checked={value.strategy === id}
            className="bot-strategy"
            onClick={() => onChange({ ...value, strategy: id })}
          >
            <strong>{STRATEGIES[id].name}</strong>
            <span>{STRATEGIES[id].description}</span>
          </button>
        ))}
      </div>
    </fieldset>
  );
}

function BotSetup({ onStart }: { onStart: (players: Players, mountain: Mountain) => void }) {
  const [watch, setWatch] = useState(false);
  const [side, setSide] = useState<'I' | 'B' | 'random'>('I');
  const [bot, setBot] = useState<BotSpec>({ difficulty: 'normal', strategy: 'balanced' });
  const [ivory, setIvory] = useState<BotSpec>({ difficulty: 'hard', strategy: 'summit' });
  const [basalt, setBasalt] = useState<BotSpec>({ difficulty: 'hard', strategy: 'hunter' });
  const [classic, setClassic] = useState(false);
  const [flags, setFlags] = useState<Partial<Record<ExperimentKey, boolean>>>({});

  const start = () => {
    const mountain: Mountain = classic
      ? { ...flags, kind: 'classic' }
      : { ...flags, kind: 'seed', seed: randomSeed() };
    let players: Players;
    if (watch) {
      players = { I: { kind: 'bot', spec: ivory }, B: { kind: 'bot', spec: basalt } };
    } else {
      const team = side === 'random' ? (randomSeed() % 2 === 0 ? 'I' : 'B') : side;
      players =
        team === 'I'
          ? { I: { kind: 'human' }, B: { kind: 'bot', spec: bot } }
          : { I: { kind: 'bot', spec: bot }, B: { kind: 'human' } };
    }
    onStart(players, mountain);
  };

  return (
    <div className="page-page bot-setup">
      <h1>{watch ? 'Watch two bots' : 'Play the bot'}</h1>
      <div className="segmented" role="group" aria-label="Mode">
        <button type="button" aria-pressed={!watch} onClick={() => setWatch(false)}>
          Play the bot
        </button>
        <button type="button" aria-pressed={watch} onClick={() => setWatch(true)}>
          Watch bot vs bot
        </button>
      </div>

      {watch ? (
        <div className="bot-pair">
          <BotPicker label="Ivory bot" value={ivory} onChange={setIvory} />
          <BotPicker label="Basalt bot" value={basalt} onChange={setBasalt} />
        </div>
      ) : (
        <>
          <fieldset className="bot-picker">
            <legend>You play</legend>
            <div className="segmented" role="group" aria-label="Your side">
              {(
                [
                  ['I', 'Ivory (moves first)'],
                  ['B', 'Basalt'],
                  ['random', 'Random'],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  aria-pressed={side === id}
                  onClick={() => setSide(id)}
                >
                  {label}
                </button>
              ))}
            </div>
          </fieldset>
          <BotPicker label="Bot" value={bot} onChange={setBot} />
        </>
      )}

      <fieldset className="bot-picker">
        <legend>Mountain</legend>
        <div className="segmented" role="group" aria-label="Mountain">
          <button type="button" aria-pressed={!classic} onClick={() => setClassic(false)}>
            New mountain
          </button>
          <button type="button" aria-pressed={classic} onClick={() => setClassic(true)}>
            Classic
          </button>
        </div>
        {EXPERIMENTS.map((e) => (
          <label className="game-check" key={e.key} title={e.description}>
            <input
              type="checkbox"
              checked={flags[e.key] === true}
              disabled={e.seedOnly && classic}
              onChange={(event) => setFlags(withExperiment(flags, e.key, event.target.checked))}
            />
            {e.label} <span className="game-hint">· {e.description}</span>
          </label>
        ))}
      </fieldset>

      <p>
        <button type="button" className="btn btn-primary" onClick={start}>
          {watch ? 'Start watching' : 'Start the game'}
        </button>
      </p>
      <p className="game-hint">
        Want numbers instead? Run{' '}
        <code className="mono">pnpm --filter @halvard/ai arena -- --all</code> to play every
        strategy against every other.
      </p>
    </div>
  );
}
